import { Edge } from 'reactflow';
import { IconDefinition, faPause, faPlay } from '@fortawesome/pro-regular-svg-icons';
import { faBolt } from '@fortawesome/pro-solid-svg-icons';
import { SendEmailNodeData, SendSmsNodeData } from 'assets/js/pages/engage/workflows/actions/types';
import { NodeWithData, ReactFlowState } from 'assets/js/pages/engage/workflows/utils/types';
import {
    EdgeTypeEnum,
    WorkflowActionTypeEnum,
    WorkflowUtilityNodeTypeEnum,
} from 'constants/workflows';
import { WorkflowStatus } from 'types/domain';

interface BuildEdgesOptions {
    isPreview: boolean;
}
const buildEdges = (nodes: NodeWithData[], options?: BuildEdgesOptions) => {
    let edges: Edge[] = [];

    for (const node of nodes) {
        let edgeType = EdgeTypeEnum.ADD_STEP;
        if (!node.data || node.data?.parentId === null) continue;
        const parentNode = nodes.find(n => n.id === node.data?.parentId);

        if (
            options?.isPreview ||
            node.type === WorkflowUtilityNodeTypeEnum.BRANCH_CONDITION ||
            node.type === WorkflowUtilityNodeTypeEnum.CHOOSE_ACTION ||
            parentNode?.type === WorkflowUtilityNodeTypeEnum.CHOOSE_ACTION ||
            parentNode?.type === WorkflowUtilityNodeTypeEnum.BRANCH
        ) {
            edgeType = EdgeTypeEnum.BASE;
        }

        edges = [
            ...edges,
            {
                id: `${node.data.parentId}->${node.id}`,
                source: node.data.parentId,
                target: node.id,
                type: edgeType,
                focusable: false,
                selected: false,
                style: { opacity: 0 },
            },
        ];
    }

    return edges;
};

const setRemoteStepIdsOnNodes = (
    localNodes: NodeWithData[],
    remoteNodes: NodeWithData[],
    updateNode: ReactFlowState['updateNode'],
) => {
    for (const localNode of localNodes) {
        const remoteNode = remoteNodes.find(n => n.id === localNode.id);
        if (
            !remoteNode ||
            !localNode.data ||
            !remoteNode.data ||
            localNode.data.stepId === remoteNode.data.stepId
        )
            continue;

        // set the data.stepId if we have one that didn't exist before
        updateNode(localNode.id, {
            ...localNode,
            data: {
                ...localNode.data,
                stepId: remoteNode.data.stepId,
            },
        });
    }
};

const setRemoteMessageIdsOnNodes = (
    localNodes: NodeWithData[],
    remoteNodes: NodeWithData[],
    updateNode: ReactFlowState['updateNode'],
) => {
    for (const localNode of localNodes) {
        const remoteNode = remoteNodes.find(n => n.id === localNode.id);
        if (!localNode.data || !remoteNode?.data) continue;

        if (remoteNode.type === WorkflowActionTypeEnum.SEND_EMAIL) {
            const { messageId: localId } = localNode.data as SendEmailNodeData;
            const { messageId: remoteId } = remoteNode.data as SendEmailNodeData;

            if (localId !== remoteId) {
                updateNode(localNode.id, {
                    ...localNode,
                    data: {
                        ...localNode.data,
                        stepId: remoteNode.data?.stepId,
                        messageId: remoteId,
                    } as SendEmailNodeData,
                });
            }
        }

        if (remoteNode.type === WorkflowActionTypeEnum.SEND_SMS) {
            const { messageId: localId } = localNode.data as SendSmsNodeData;
            const { messageId: remoteId } = remoteNode.data as SendSmsNodeData;

            if (localId !== remoteId) {
                updateNode(localNode.id, {
                    ...localNode,
                    data: {
                        ...localNode.data,
                        stepId: remoteNode.data?.stepId,
                        messageId: remoteId,
                    } as SendSmsNodeData,
                });
            }
        }
    }
};

interface GetNextStatusDataReturn {
    buttonText: string;
    nextStatus: WorkflowStatus;
    icon: IconDefinition;
}
const getNextStatusData = (status: WorkflowStatus): GetNextStatusDataReturn => {
    switch (status) {
        case 'draft':
            return {
                buttonText: 'Publish',
                nextStatus: 'active',
                icon: faBolt,
            };
        case 'active':
            return {
                buttonText: 'Pause',
                nextStatus: 'paused',
                icon: faPause,
            };
        case 'paused':
        default:
            return {
                buttonText: 'Resume',
                nextStatus: 'active',
                icon: faPlay,
            };
    }
};

const traverseUpTree = (
    nodes: NodeWithData[],
    id?: string | null,
    callback?: (node: NodeWithData) => void,
) => {
    const node = nodes.find(n => n.id === id);
    if (!id || !node) return;

    callback?.(node);
    traverseUpTree(nodes, node?.data?.parentId, callback);
};

const traverseDownTree = (
    nodes: NodeWithData[],
    id?: string | null,
    callback?: (node: NodeWithData) => void,
) => {
    const foundNodeIds = nodes.filter(n => n.id === id).map(n => n.id);
    const children = nodes.filter(n => foundNodeIds.includes(n.data?.parentId || ''));

    children.forEach(child => {
        callback?.(child);
        traverseDownTree(nodes, child.id, callback);
    });
};

export {
    buildEdges,
    getNextStatusData,
    setRemoteStepIdsOnNodes,
    setRemoteMessageIdsOnNodes,
    traverseUpTree,
    traverseDownTree,
};
